import { useState } from 'react';

import {
  MAX_EMOTE_SIZE,
  MAX_FADE_SECONDS,
  MAX_FONT_SIZE,
  MIN_EMOTE_SIZE,
  MIN_FADE_SECONDS,
  MIN_FONT_SIZE,
} from '../api/preference-bounds.js';
import type {
  ChatHistoryMode,
  ChatPreferences,
  ConsoleTheme,
  TimestampFormat,
} from '../api/types.js';
import { usePreferences } from '../chat/use-preferences.js';

/**
 * How chat looks, chosen per person (ARCHITECTURE.md §22, §65).
 *
 * Every control is a real form control with a real label. §65 forbids meaning
 * carried by appearance alone, so each toggle is a checkbox that announces its
 * own state rather than a pair of styled buttons where only colour says which
 * is selected.
 */

const TIMESTAMP_CHOICES: { value: TimestampFormat; label: string; example: string }[] = [
  { value: '24h_seconds', label: '24-hour with seconds', example: '14:32:09' },
  { value: '24h', label: '24-hour', example: '14:32' },
  { value: '12h_seconds', label: '12-hour with seconds', example: '2:32:09 PM' },
  { value: '12h', label: '12-hour', example: '2:32 PM' },
  { value: 'hidden', label: 'Hidden', example: 'no timestamp' },
];

/**
 * The three services, and what each actually covers.
 *
 * Stated rather than implied: FrankerFaceZ has no concept of a Kick room, so
 * a workspace with only Kick connected gains nothing by enabling it, and a
 * toggle that says otherwise is the kind of claim §18 forbids.
 */
const EMOTE_PROVIDERS: { key: keyof ChatPreferences; label: string; hint: string }[] = [
  { key: 'sevenTvEmotes', label: '7TV', hint: 'Twitch and Kick.' },
  { key: 'bttvEmotes', label: 'BetterTTV', hint: 'Twitch and Kick.' },
  { key: 'ffzEmotes', label: 'FrankerFaceZ', hint: 'Twitch only.' },
];

const THEME_CHOICES: { value: ConsoleTheme; label: string; hint: string }[] = [
  { value: 'dark', label: 'Dark', hint: "Connaro's default broadcast-control appearance." },
  { value: 'light', label: 'Light', hint: 'For a bright room or a daylight desk.' },
  {
    value: 'twitch',
    label: 'Twitch',
    hint: "Dark, with Twitch's purple in place of Connaro's blue.",
  },
];

const HISTORY_CHOICES: { value: ChatHistoryMode; label: string; hint: string }[] = [
  { value: 'all', label: 'All', hint: 'Load whatever your plan has retained.' },
  { value: 'live_only', label: 'Live only', hint: 'Start empty; show messages as they arrive.' },
  { value: 'off', label: 'Off', hint: 'Never load history.' },
];

const TOGGLES: { key: keyof ChatPreferences; label: string; hint: string }[] = [
  { key: 'showPlatformIcons', label: 'Platform icons', hint: 'The brand mark on each message.' },
  { key: 'showProfilePictures', label: 'Profile pictures', hint: "The speaker's avatar." },
  {
    key: 'highlightMentions',
    label: 'Highlight mentions',
    hint: 'Mark messages that name you.',
  },
  {
    key: 'alternatingBackgrounds',
    label: 'Alternating backgrounds',
    hint: 'Shade every other row, to follow a line across a wide screen.',
  },
  {
    key: 'randomNameColors',
    label: 'Colour speaker names',
    hint: 'A stable colour per person, from a palette checked for readability.',
  },
  {
    key: 'showQuickActions',
    label: 'Moderation shortcut',
    hint: 'Link each message to Moderator. Only shown if you may moderate.',
  },
  {
    key: 'dimChatHistory',
    label: 'Dim loaded history',
    hint: 'Fade messages loaded from history, so live traffic stands out.',
  },
];

export function ChatAppearance(): React.JSX.Element {
  const { preferences, update, loaded } = usePreferences();
  const [error, setError] = useState<string | null>(null);

  const apply = (patch: Partial<ChatPreferences>): void => {
    setError(null);
    void update(patch).catch(() => {
      // Said in words, and the control springs back to the stored value —
      // silently keeping a change that was not saved is the worse failure.
      setError('Connaro could not save that preference. Your other settings are unchanged.');
    });
  };

  return (
    <section className="panel" aria-labelledby="appearance-heading">
      <div className="panel__header">
        <h2 className="panel__title" id="appearance-heading">
          Chat appearance
        </h2>
      </div>

      <div className="panel__body">
        <p className="field__hint">
          These follow you between workspaces and affect only your own Console — not what anyone
          else on your team sees.
        </p>

        {error === null ? null : <p className="detail__unavailable">{error}</p>}

        <div className="field">
          <label className="field__label" htmlFor="font-size">
            Message size
          </label>
          <div className="appearance__row">
            <input
              id="font-size"
              type="range"
              min={MIN_FONT_SIZE}
              max={MAX_FONT_SIZE}
              step={1}
              value={preferences.fontSize}
              disabled={!loaded}
              onChange={(event) => {
                apply({ fontSize: Number(event.target.value) });
              }}
            />
            {/* The number in words as well as a slider position (§65). */}
            <output className="appearance__value" htmlFor="font-size">
              {preferences.fontSize}px
            </output>
          </div>
        </div>

        <fieldset className="field appearance__fieldset">
          <legend className="field__label">Theme</legend>
          {THEME_CHOICES.map((choice) => (
            <label key={choice.value} className="appearance__choice">
              <input
                type="radio"
                name="console-theme"
                value={choice.value}
                checked={preferences.theme === choice.value}
                disabled={!loaded}
                onChange={() => {
                  apply({ theme: choice.value });
                }}
              />
              <span>{choice.label}</span>
              <span className="appearance__example">{choice.hint}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="field appearance__fieldset">
          <legend className="field__label">Timestamps</legend>
          {TIMESTAMP_CHOICES.map((choice) => (
            <label key={choice.value} className="appearance__choice">
              <input
                type="radio"
                name="timestamp-format"
                value={choice.value}
                checked={preferences.timestampFormat === choice.value}
                disabled={!loaded}
                onChange={() => {
                  apply({ timestampFormat: choice.value });
                }}
              />
              <span>{choice.label}</span>
              <span className="appearance__example">{choice.example}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="field appearance__fieldset">
          <legend className="field__label">Show in each message</legend>
          {TOGGLES.map((toggle) => (
            <label key={toggle.key} className="appearance__choice">
              <input
                type="checkbox"
                checked={preferences[toggle.key] === true}
                disabled={!loaded}
                onChange={(event) => {
                  apply({ [toggle.key]: event.target.checked });
                }}
              />
              <span>{toggle.label}</span>
              <span className="appearance__example">{toggle.hint}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="field appearance__fieldset">
          <legend className="field__label">Chat history</legend>
          {HISTORY_CHOICES.map((choice) => (
            <label key={choice.value} className="appearance__choice">
              <input
                type="radio"
                name="chat-history"
                value={choice.value}
                checked={preferences.chatHistory === choice.value}
                disabled={!loaded}
                onChange={() => {
                  apply({ chatHistory: choice.value });
                }}
              />
              <span>{choice.label}</span>
              <span className="appearance__example">{choice.hint}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="field appearance__fieldset">
          <legend className="field__label">Third-party emotes</legend>
          <p className="field__hint">
            Connaro fetches these on your behalf, so they are off until you ask for them.
          </p>
          {EMOTE_PROVIDERS.map((provider) => (
            <label key={provider.key} className="appearance__choice">
              <input
                type="checkbox"
                checked={preferences[provider.key] === true}
                disabled={!loaded}
                onChange={(event) => {
                  apply({ [provider.key]: event.target.checked });
                }}
              />
              <span>{provider.label}</span>
              <span className="appearance__example">{provider.hint}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="field appearance__fieldset">
          <legend className="field__label">Name paints</legend>
          <label className="appearance__choice">
            <input
              type="checkbox"
              checked={preferences.sevenTvPaints}
              disabled={!loaded}
              onChange={(event) => {
                apply({ sevenTvPaints: event.target.checked });
              }}
            />
            <span>7TV name paints</span>
            <span className="appearance__example">
              Twitch only. Connaro looks up each viewer, so it is a request per person rather than
              one per channel.
            </span>
          </label>
        </fieldset>

        <div className="field">
          <label className="field__label" htmlFor="emote-size">
            Emote size
          </label>
          <div className="appearance__row">
            <input
              id="emote-size"
              type="range"
              min={MIN_EMOTE_SIZE}
              max={MAX_EMOTE_SIZE}
              step={2}
              value={preferences.emoteSize}
              disabled={
                !loaded ||
                !(preferences.sevenTvEmotes || preferences.bttvEmotes || preferences.ffzEmotes)
              }
              onChange={(event) => {
                apply({ emoteSize: Number(event.target.value) });
              }}
            />
            <output className="appearance__value" htmlFor="emote-size">
              {preferences.emoteSize}px
            </output>
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="fade-out">
            Fade messages out
          </label>
          <div className="appearance__row">
            <input
              id="fade-out"
              type="range"
              /*
               * The floor is 0 (never) and the real range starts at
               * MIN_FADE_SECONDS, so the track has a deliberate gap: dragging
               * off zero jumps to the shortest duration that can still be read
               * rather than to one second.
               */
              min={0}
              max={MAX_FADE_SECONDS}
              step={5}
              value={preferences.fadeOutSeconds}
              disabled={!loaded}
              onChange={(event) => {
                const raw = Number(event.target.value);
                apply({ fadeOutSeconds: raw === 0 ? 0 : Math.max(raw, MIN_FADE_SECONDS) });
              }}
            />
            <output className="appearance__value" htmlFor="fade-out">
              {preferences.fadeOutSeconds === 0
                ? 'Never'
                : `${String(preferences.fadeOutSeconds)}s`}
            </output>
          </div>
          <p className="field__hint">
            Useful for a chat kept on a second screen during a broadcast. Messages fade where they
            are rather than being removed, so the feed does not jump while you read.
          </p>
        </div>
      </div>
    </section>
  );
}
